import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { tournamentTypeLabel } from "../lib/playerLabels";
import DuplicateTournamentDialog from "../components/DuplicateTournamentDialog";
import TournamentBottomNav from "../components/TournamentBottomNav";
import TournamentFormatConfigSection from "../components/TournamentFormatConfig";
import GuideHelpButton from "../components/GuideHelpButton";
import {
  api,
  ApiError,
  type Tournament,
  type TournamentAdminAssignment,
  type TournamentDuplicatePreview,
  type TournamentFormatConfig,
  type TournamentSettings,
  type TournamentType,
} from "../lib/api";

const TOURNAMENT_TYPES: Array<{ value: TournamentType; label: string }> = [
  { value: "ladder", label: "Escalera" },
  { value: "round_robin", label: "Round Robin" },
  { value: "elimination", label: "Eliminatoria" },
];

function formatTypeLabel(value: string): string {
  return tournamentTypeLabel(value);
}

const REPORTING_MODES = [
  { value: "disabled", label: "Solo admin" },
  {
    value: "player_report_requires_admin",
    label: "Jugador reporta · admin aprueba",
  },
];

export default function AdminTournamentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const { token } = useAuth();

  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [settings, setSettings] = useState<TournamentSettings | null>(null);
  const [formatConfig, setFormatConfig] =
    useState<TournamentFormatConfig | null>(null);
  const [admins, setAdmins] = useState<TournamentAdminAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingTournament, setSavingTournament] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [canDuplicate, setCanDuplicate] = useState(false);
  const [duplicatePreview, setDuplicatePreview] = useState<TournamentDuplicatePreview | null>(null);
  const [duplicateOpen, setDuplicateOpen] = useState(false);

  const load = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.tournaments.get(token, tournamentId);
      setTournament(response.data.tournament);
      setCanDuplicate(response.data.can_duplicate === true);
      setDuplicatePreview(response.data.duplicate_preview ?? null);
      setSettings(response.data.settings);
      setFormatConfig(response.data.format_config);
      setAdmins(response.data.admins);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar el torneo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, tournamentId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleTournamentSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || !tournament) return;
    setMessage(null);
    setSavingTournament(true);
    try {
      const response = await api.tournaments.update(token, tournament.id, {
        name: tournament.name,
        tournament_type: tournament.tournament_type as TournamentType,
        venue_name: tournament.venue_name,
        start_date: tournament.start_date,
        end_date: tournament.end_date,
        registration_deadline: tournament.registration_deadline,
        registration_cost: tournament.registration_cost,
        official_ball: tournament.official_ball,
        play_mode: tournament.play_mode === "doubles" ? "doubles" : "singles",
        is_public: tournament.is_public,
        is_federation_official: tournament.is_federation_official,
        description: tournament.description,
      });
      setTournament(response.data.tournament);
      setMessage("Torneo actualizado.");
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo actualizar el torneo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setSavingTournament(false);
    }
  }

  async function handleSettingsSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || !settings) return;
    setMessage(null);
    setSavingSettings(true);
    try {
      const response = await api.tournaments.updateSettings(
        token,
        settings.tournament_id,
        {
          allow_player_result_reporting: settings.allow_player_result_reporting,
          result_reporting_mode: settings.result_reporting_mode,
          auto_generate_announcements: settings.auto_generate_announcements,
          email_notifications_enabled: settings.email_notifications_enabled,
          whatsapp_notifications_enabled: settings.whatsapp_notifications_enabled,
          allow_post_publish_changes: settings.allow_post_publish_changes,
        },
      );
      setSettings(response.data.settings);
      setMessage("Configuración guardada.");
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo actualizar la configuración.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setSavingSettings(false);
    }
  }

  if (Number.isNaN(tournamentId)) {
    return (
      <section className="card">
        <p className="form-error">ID de torneo inválido.</p>
      </section>
    );
  }

  if (loading) {
    return (
      <section className="card">
        <p className="muted">Cargando…</p>
      </section>
    );
  }

  if (error && !tournament) {
    return (
      <section className="card">
        <p className="form-error">{error}</p>
        <Link className="btn btn--ghost" to="/admin/tournaments">
          Volver
        </Link>
      </section>
    );
  }

  if (!tournament || !settings || !formatConfig) {
    return null;
  }

  return (
    <>
      <section className="card">
        <div className="card__header-row">
          <div>
            <h1 className="card__title"><span className="hint-label">{tournament.name}<GuideHelpButton /></span></h1>
            <p className="card__subtitle">
              <span className="kbd">
                {formatTypeLabel(tournament.tournament_type)}
              </span>{" "}
              <span className="kbd">{tournament.status}</span>{" "}
              <span className="kbd kbd--ok">
                Formato: {formatTypeLabel(formatConfig.format_type)}
              </span>
            </p>
          </div>
          <div className="form__actions tournament-hero-actions">
            <Link
              className="btn"
              to={`/admin/tournaments/${tournament.id}/players`}
            >
              Jugadores
            </Link>
            <Link
              className="btn"
              to={`/admin/tournaments/${tournament.id}/groups`}
            >
              Grupos
            </Link>
            {tournament.tournament_type === "ladder" && (
              <Link
                className="btn"
                to={`/admin/tournaments/${tournament.id}/rounds`}
              >
                Draw
              </Link>
            )}
            <Link
              className="btn"
              to={`/admin/tournaments/${tournament.id}/schedule`}
            >
              Calendario
            </Link>
            <Link
              className="btn"
              to={`/admin/tournaments/${tournament.id}/standings`}
            >
              Standings
            </Link>
            <Link
              className="btn"
              to={`/admin/tournaments/${tournament.id}/communications`}
            >
              Comunicados
            </Link>
            <Link
              className="btn"
              to={`/admin/tournaments/${tournament.id}/rules`}
            >
              Reglamento
            </Link>
            {/* Solo si los jugadores reportan sus resultados; apagado, confunde. */}
            {settings.allow_player_result_reporting ? (
              <Link
                className="btn"
                to={`/admin/tournaments/${tournament.id}/result-reports`}
              >
                Resultados reportados
              </Link>
            ) : null}
            {/* Duplicar (Eduardo 2026-10-03): pide nombre y fechas nuevos. */}
            {canDuplicate ? (
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setDuplicateOpen(true)}
              >
                Duplicar
              </button>
            ) : null}
            {/* «Archivar» se reemplazó por «Cerrar torneo» en el Resumen
                (Eduardo 2026-09-27). */}
            <Link className="btn btn--ghost" to={`/admin/tournaments/${tournament.id}`}>
              {tournament.status === "closed" ? "Reabrir (en el Resumen)" : "Cerrar torneo (en el Resumen)"}
            </Link>
          </div>
        </div>
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
        {message && <p className="form-message">{message}</p>}
      </section>

      <section className="card">
        <h2 className="card__title">Datos del torneo</h2>
        <form className="form" onSubmit={handleTournamentSubmit}>
          <label>
            Nombre
            <input
              type="text"
              required
              value={tournament.name}
              onChange={(e) =>
                setTournament({ ...tournament, name: e.target.value })
              }
              disabled={savingTournament}
            />
          </label>
          <label>
            Tipo
            <select
              value={tournament.tournament_type}
              onChange={(e) =>
                setTournament({
                  ...tournament,
                  tournament_type: e.target.value,
                })
              }
              disabled={savingTournament}
            >
              {TOURNAMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Modalidad
            <select
              value={tournament.play_mode === "doubles" ? "doubles" : "singles"}
              onChange={(e) =>
                setTournament({
                  ...tournament,
                  play_mode: e.target.value,
                })
              }
              disabled={savingTournament}
            >
              <option value="singles">Singles · jugador individual</option>
              <option value="doubles">Dobles · parejas de 2 jugadores</option>
            </select>
            <span className="muted">
              Si ya hay participantes, el backend protege contra cambios de modalidad.
            </span>
          </label>
          <label>
            Sede
            <input
              type="text"
              value={tournament.venue_name ?? ""}
              onChange={(e) =>
                setTournament({ ...tournament, venue_name: e.target.value })
              }
              disabled={savingTournament}
            />
          </label>
          <label>
            Fecha inicio
            <input
              type="date"
              value={tournament.start_date ?? ""}
              onChange={(e) =>
                setTournament({ ...tournament, start_date: e.target.value || null })
              }
              disabled={savingTournament}
            />
          </label>
          <label>
            Fecha fin
            <input
              type="date"
              value={tournament.end_date ?? ""}
              onChange={(e) =>
                setTournament({ ...tournament, end_date: e.target.value || null })
              }
              disabled={savingTournament}
            />
          </label>
          <label>
            Fecha cierre de inscripciones
            <input
              type="date"
              value={tournament.registration_deadline ?? ""}
              onChange={(e) =>
                setTournament({
                  ...tournament,
                  registration_deadline: e.target.value || null,
                })
              }
              disabled={savingTournament}
            />
          </label>
          <label>
            Costo
            <input
              type="text"
              placeholder="$1,500 MXN"
              value={tournament.registration_cost ?? ""}
              onChange={(e) =>
                setTournament({ ...tournament, registration_cost: e.target.value })
              }
              disabled={savingTournament}
            />
          </label>
          <label>
            Pelota oficial
            <input
              type="text"
              placeholder="Wilson US Open / Head Tour / Penn..."
              value={tournament.official_ball ?? ""}
              onChange={(e) =>
                setTournament({ ...tournament, official_ball: e.target.value })
              }
              disabled={savingTournament}
            />
          </label>
          <div className="form__option-stack">
            <label className="form__checkbox form__checkbox--boxed">
              <input
                type="checkbox"
                checked={Boolean(tournament.is_public)}
                onChange={(e) =>
                  setTournament({ ...tournament, is_public: e.target.checked })
                }
                disabled={savingTournament}
              />
              <span>
                Torneo público
                <small>Activado = aparece en torneos123.com y en «Torneos públicos» mientras tenga fecha de inicio e inscripciones abiertas.</small>
              </span>
            </label>
            <label className="form__checkbox form__checkbox--boxed">
              <input
                type="checkbox"
                checked={Boolean(tournament.is_federation_official)}
                onChange={(e) =>
                  setTournament({
                    ...tournament,
                    is_federation_official: e.target.checked,
                  })
                }
                disabled={savingTournament}
              />
              <span>
                Torneo oficial de Federación Mexicana de Tennis
                <small>Marca torneos avalados/oficiales para reportes y distintivos.</small>
              </span>
            </label>
          </div>
          <button className="btn" type="submit" disabled={savingTournament}>
            {savingTournament ? "Guardando…" : "Guardar cambios"}
          </button>
        </form>
      </section>

      {token && (
        <TournamentFormatConfigSection
          token={token}
          tournamentId={tournament.id}
          initialConfig={formatConfig}
          onChange={(next) => {
            setFormatConfig(next);
            // Keep the parent tournament_type in sync with the chosen format
            // so existing flows (badges, defaults) stay consistent.
            if (next.format_type !== tournament.tournament_type) {
              setTournament({
                ...tournament,
                tournament_type: next.format_type,
              });
            }
          }}
        />
      )}

      <section className="card">
        <div className="card__header-row">
          <div>
            <h2 className="card__title">Próximos pasos</h2>
            <p className="card__subtitle">
              Una vez confirmado el formato, el Draw generará enfrentamientos o
              bracket automáticamente.
            </p>
          </div>
          <div className="form__actions">
            {tournament.tournament_type === "ladder" ? (
              <Link
                className="btn"
                to={`/admin/tournaments/${tournament.id}/rounds`}
              >
                Próximo: Draw
              </Link>
            ) : (
              <button
                type="button"
                className="btn btn--ghost"
                disabled
                title="Disponible para Escalera; otros formatos en próximos sprints"
              >
                Próximo: Draw
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="card">
        <h2 className="card__title">Configuración</h2>
        <form className="form" onSubmit={handleSettingsSubmit}>
          {/* «¿Este torneo usa calendario de canchas?» se decide solo en la
              sección Calendario (Eduardo 2026-10-03). */}
          <label className="form__checkbox">
            <input
              type="checkbox"
              checked={settings.allow_player_result_reporting}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  allow_player_result_reporting: e.target.checked,
                })
              }
              disabled={savingSettings}
            />
            Permitir reporte de resultados por jugadores
          </label>
          <label>
            Modo de reporte de resultados
            <select
              value={settings.result_reporting_mode}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  result_reporting_mode: e.target.value,
                })
              }
              disabled={savingSettings}
            >
              {REPORTING_MODES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <label className="form__checkbox">
            <input
              type="checkbox"
              checked={settings.auto_generate_announcements}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  auto_generate_announcements: e.target.checked,
                })
              }
              disabled={savingSettings}
            />
            Generar anuncios automáticamente
          </label>
          <label className="form__checkbox">
            <input
              type="checkbox"
              checked={settings.email_notifications_enabled}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  email_notifications_enabled: e.target.checked,
                })
              }
              disabled={savingSettings}
            />
            Notificaciones por email
          </label>
          <label className="form__checkbox">
            <input
              type="checkbox"
              checked={settings.whatsapp_notifications_enabled}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  whatsapp_notifications_enabled: e.target.checked,
                })
              }
              disabled={savingSettings}
            />
            Notificaciones por WhatsApp
          </label>
          <label className="form__checkbox">
            <input
              type="checkbox"
              checked={settings.allow_post_publish_changes}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  allow_post_publish_changes: e.target.checked,
                })
              }
              disabled={savingSettings}
            />
            Permitir cambios después de publicar
          </label>
          <button className="btn" type="submit" disabled={savingSettings}>
            {savingSettings ? "Guardando…" : "Guardar configuración"}
          </button>
        </form>
      </section>

      <section className="card">
        <h2 className="card__title">Administradores</h2>
        {admins.length === 0 ? (
          <p className="muted">Sin administradores asignados.</p>
        ) : (
          <ul className="list">
            {admins.map((a) => (
              <li key={a.id}>
                <strong>user #{a.user_id}</strong>
                {a.role_in_tournament && (
                  <span className="muted"> · {a.role_in_tournament}</span>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="muted">
          La asignación de administradores se realiza desde el panel
          superadmin.
        </p>
      </section>

      <section className="card">
        <Link className="btn btn--ghost" to="/admin/tournaments">
          ← Volver a mis torneos
        </Link>
      </section>

      {duplicateOpen && token ? (
        <DuplicateTournamentDialog
          token={token}
          tournament={tournament}
          preview={duplicatePreview}
          onClose={() => setDuplicateOpen(false)}
        />
      ) : null}
      <TournamentBottomNav tournamentId={tournament.id} />
    </>
  );
}
